import type { Family, FamilyGroup, InviteLog } from "@teamna/shared";
import { useState } from "react";
import { Button, Checkbox, CopyIcon, Notice, showToast } from "../../components/index.ts";
import { WhatsAppIcon } from "../../components/PhoneSignIn.tsx";
import { canInvite, dayAndTime, familyName, householdWord, inviteMessage, inviteReady, inviteWarning, type Words } from "./model.ts";
import type { FamilyActions } from "./types.ts";

/**
 * The Invites tab: any family, any time. Nothing here is a last step: the owner sends to the
 * families that are ready, and more later. The preview is what the first chosen parent reads.
 */
export function InvitesView({
  families,
  groups,
  words,
  log,
  timezone,
  actions,
  onError,
}: {
  families: Family[];
  groups: FamilyGroup[];
  words: Words;
  log: InviteLog | null;
  timezone: string;
  actions: FamilyActions;
  onError: (error: unknown) => void;
}) {
  const [chosen, setChosen] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  const ready = families.filter((family) => inviteReady(family, groups, words));
  const many = householdWord(2, words);
  const pageName = many[0]!.toUpperCase() + many.slice(1);
  const picked = families.filter((family) => chosen.includes(family.id));
  const adults = picked.reduce((sum, family) => sum + family.adults.filter((adult) => adult.phoneE164 && !adult.joined).length, 0);
  const preview = picked[0] ?? ready[0] ?? families.find((family) => family.adults.some((adult) => adult.phoneE164)) ?? null;
  const previewAdult = preview?.adults.find((adult) => adult.phoneE164) ?? null;

  const tiles = [
    { value: families.filter((family) => family.invite === "none").length, label: "not invited" },
    { value: families.filter((family) => family.invite === "invited").length, label: "invited, not in yet" },
    { value: families.filter((family) => family.invite === "joined").length, label: "joined" },
  ];

  async function send() {
    if (picked.length === 0) return;
    setSending(true);
    try {
      const result = await actions.invite(picked.map((family) => family.id));
      setChosen([]);
      showToast(
        result.simulated
          ? `Invite recorded for ${result.sent} ${result.sent === 1 ? words.member : words.members}. WhatsApp is not connected yet, so nothing was sent.`
          : `Invite sent to ${result.sent} ${result.sent === 1 ? words.member : words.members}. You see in ${pageName} who joined.`,
        { tone: result.simulated ? "info" : "success" },
      );
    } catch (error) {
      onError(error);
    } finally {
      setSending(false);
    }
  }

  async function copyLink() {
    if (!log) return;
    try {
      await navigator.clipboard.writeText(log.url);
      showToast("Link copied.", { tone: "info" });
    } catch {
      showToast(`Copy this link: ${log.url}`, { tone: "info" });
    }
  }

  return (
    <section aria-label="Invites" className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-card bg-paper px-4 py-3 shadow-card ring-1 ring-line">
            <p className="text-[26px] leading-none font-bold text-ink tabular-nums">{tile.value}</p>
            <p className="mt-1 text-[13px] text-ink-2">{tile.label}</p>
          </div>
        ))}
      </div>
      <p className="text-[14px] text-ink-2">
        Send when you are ready, to the {many} you choose. You can send to more {many} later. Nothing here is a last step.
      </p>
      {log?.simulated ? (
        <Notice tone="info" title="WhatsApp is not connected yet">
          An invite is recorded, not sent. You can share the join link yourself in the meantime.
        </Notice>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-3 rounded-card bg-paper p-4 shadow-card ring-1 ring-line">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-[17px]">Choose {many}</h2>
            <Button variant="ghost" onClick={() => setChosen(ready.map((family) => family.id))} disabled={ready.length === 0}>
              Choose all ready ({ready.length})
            </Button>
          </div>
          <ul className="flex flex-col divide-y divide-line">
            {families.map((family) => {
              const allowed = canInvite(family);
              const warning = inviteWarning(family, groups, words);
              const children = !words.forChildren
                ? null
                : family.children.length
                ? family.children
                    .map((child) => `${child.name} (${groups.find((group) => group.id === child.groupId)?.name ?? (child.placementStatus === "unsure" ? "not sure yet" : `no ${words.group}`)})`)
                    .join(", ")
                : "No children yet";
              const status = family.invite === "joined" ? "Joined" : family.invite === "invited" ? "Invited. Send again?" : "Not invited";
              return (
                <li key={family.id} className="py-2.5">
                  <Checkbox
                    label={`${familyName(family)}: ${status}`}
                    hint={[children, warning].filter(Boolean).join(". ")}
                    checked={chosen.includes(family.id)}
                    disabled={!allowed}
                    onChange={(checked) =>
                      setChosen((current) => (checked ? [...current, family.id] : current.filter((id) => id !== family.id)))
                    }
                  />
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center gap-3 border-t border-line pt-3">
            <Button variant="whatsapp" onClick={() => void send()} disabled={picked.length === 0} loading={sending} className="gap-2">
              <WhatsAppIcon className="size-4" />
              {picked.length ? `Send invite to ${adults} ${adults === 1 ? words.member : words.members}` : `Choose ${many} to invite`}
            </Button>
            <span className="text-[13px] text-ink-2">
              {picked.length ? `${picked.length} ${householdWord(picked.length, words)} chosen.` : "Nothing is sent until you press it."}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-card bg-paper p-4 shadow-card ring-1 ring-line">
            <h2 className="text-[17px]">{previewAdult ? `What ${previewAdult.name.split(/\s+/)[0]} reads` : "The message"}</h2>
            <div className="mt-3 rounded-2xl bg-wa-chat p-3">
              <div className="bubble bubble-wa max-w-[95%] rounded-2xl px-3 py-2 text-[14px] leading-relaxed whitespace-pre-line text-ink" dir="auto">
                {preview && previewAdult && log
                  ? inviteMessage(preview, previewAdult, groups, { businessName: log.businessName, url: log.url, code: log.code, words })
                  : `Choose a ${householdWord(1, words)} to see the message.`}
              </div>
            </div>
            {log ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink-2">{log.url}</span>
                <Button variant="secondary" onClick={() => void copyLink()} className="gap-2">
                  <CopyIcon size={16} />
                  Copy link
                </Button>
              </div>
            ) : null}
          </div>
          <div className="rounded-card bg-paper p-4 shadow-card ring-1 ring-line">
            <h2 className="text-[17px]">Sent</h2>
            {log && log.sends.length ? (
              <ul className="mt-2 flex flex-col divide-y divide-line">
                {log.sends.map((entry) => (
                  <li key={entry.id} className="flex items-center justify-between gap-3 py-2">
                    <span className="min-w-0">
                      <span className="block font-semibold text-ink" dir="auto">
                        {entry.label}
                      </span>
                      <span className="text-[13px] text-ink-2">{dayAndTime(entry.sentAt, timezone)}</span>
                    </span>
                    <span className="shrink-0 text-[13px] font-semibold text-ink-2">
                      {entry.recipients} {entry.recipients === 1 ? words.member : words.members}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[14px] text-ink-2">No invite sent yet.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
